import { formatThousandsCheckNull } from "@/lib/formatters/numbers";
import {
  createColumnConfig,
  StandardTableConfigWithGroups,
} from "@stenajs-webui/grid";
import { negotiationStatusCodeTransformer } from "../../../lib/formatters/NegotiationStatusCodeTransformer";
import { transformAccUnaccEntityToSingleLetter } from "../../../lib/transformers/UnaccompaniedTransformer";
import {
  advancedInfoColumnOrder,
  avgPriceColumnOrder,
  noLabelColumnOrder,
  routeSailingAndUnitColumnOrder,
  targetRateAndVolumesColumnOrder,
} from "./StatisticsByAdvancedColumnOrder";
import { Indent, Txt } from "@stenajs-webui/core";
import { stripLineFromSellerName } from "@/lib/transformers/SellerNameTransformer";
import { StatisticsByAdvancedListItem } from "@/gql/graphql";

export type StatisticsAdvancedDataListColumns =
  | "customerName"
  | "negStatus"
  | "custNo"
  | "custIndex"
  | "statNo"
  | "parentNo"
  | "seller"
  | "route"
  | "sailing"
  | "unit"
  | "gross"
  | "net"
  | "target"
  | "rate"
  | "volume12Month"
  | "volumeStat12Month"
  | "expVolume";

export const createByAdvancedResultTableConfig = (
  currency: string
): StandardTableConfigWithGroups<
  StatisticsByAdvancedListItem,
  StatisticsAdvancedDataListColumns
> => ({
  keyResolver: (item) => item.id,
  disableSorting: false,
  stickyHeader: true,
  stickyColumnGroups: "first",
  initialSortOrder: "customerName",
  initialSortOrderDesc: false,
  rowIndent: 2,
  columns: {
    customerName: createColumnConfig((item) => item.customerName, {
      columnLabel: "Name",
      width: "180px",
      justifyContentCell: "left",
      justifyContentHeader: "left",
    }),
    negStatus: createColumnConfig((item) => item.negotiationStatus, {
      columnLabel: "Neg status",
      minWidth: "120px",
      itemLabelFormatter: (item) =>
        negotiationStatusCodeTransformer(item ?? ""),
      justifyContentCell: "left",
      justifyContentHeader: "left",
    }),
    custNo: createColumnConfig((item) => item.custNo, {
      columnLabel: "Cust no.",
      minWidth: "90px",
    }),
    custIndex: createColumnConfig((item) => item.custIndex, {
      columnLabel: "Index",
      minWidth: "94px",
    }),
    statNo: createColumnConfig((item) => item.statNo, {
      columnLabel: "Stat no.",
      minWidth: "88px",
    }),
    parentNo: createColumnConfig((item) => item.parentNo, {
      columnLabel: "Parent no.",
      minWidth: "103px",
    }),
    seller: createColumnConfig(
      (item) => stripLineFromSellerName(item.seller ?? "").toUpperCase(),
      {
        columnLabel: "Seller",
        minWidth: "80px",
      }
    ),
    route: createColumnConfig((item) => item.routePairCode, {
      columnLabel: "Route",
      minWidth: "66px",
    }),
    sailing: createColumnConfig((item) => item.sailingCategoryCode, {
      columnLabel: "Sailing",
      minWidth: "69px",
    }),
    unit: createColumnConfig((item) => item.unitCategoryCode, {
      columnLabel: "Unit",
      minWidth: "57px",
      itemLabelFormatter: (value) =>
        transformAccUnaccEntityToSingleLetter(value ?? ""),
    }),
    gross: {
      itemValueResolver: ({ avgGrossMeterPrice }) =>
        avgGrossMeterPrice === null ? -Infinity : avgGrossMeterPrice,
      columnLabel: "Gross",
      minWidth: "64px",
      renderCell: ({ item: { avgGrossMeterPrice } }) => (
        <Txt>
          <Indent>{formatThousandsCheckNull(avgGrossMeterPrice)}</Indent>
        </Txt>
      ),
    },
    net: {
      itemValueResolver: ({ avgNetMeterPrice }) =>
        avgNetMeterPrice === null ? -Infinity : avgNetMeterPrice,
      columnLabel: "Net",
      minWidth: "64px",
      renderCell: ({ item: { avgNetMeterPrice } }) => (
        <Txt>
          <Indent>{formatThousandsCheckNull(avgNetMeterPrice)}</Indent>
        </Txt>
      ),
    },
    target: {
      itemValueResolver: ({ targetInPercent }) =>
        targetInPercent === null ? -Infinity : targetInPercent,
      columnLabel: "Target (%)",
      width: "105px",
      renderCell: ({ item: { targetInPercent } }) => (
        <Txt>
          <Indent>
            {targetInPercent === 0
              ? targetInPercent
              : formatThousandsCheckNull(targetInPercent)}
          </Indent>
        </Txt>
      ),
    },
    rate: {
      itemValueResolver: ({ actualOutcomeInPercent }) =>
        actualOutcomeInPercent === null ? -Infinity : actualOutcomeInPercent,
      columnLabel: "Rate (%)",
      width: "90px",
      renderCell: ({ item: { actualOutcomeInPercent } }) => (
        <Txt>
          <Indent>
            {actualOutcomeInPercent === 0
              ? actualOutcomeInPercent
              : formatThousandsCheckNull(actualOutcomeInPercent)}
          </Indent>
        </Txt>
      ),
    },
    volume12Month: createColumnConfig((item) => item.last12MonthVol, {
      columnLabel: "12m vol.",
      width: "95px",
      itemLabelFormatter: formatThousandsCheckNull,
    }),
    volumeStat12Month: createColumnConfig((item) => item.last12MonthStatVol, {
      columnLabel: "12m stat vol.",
      width: "110px",
      itemLabelFormatter: formatThousandsCheckNull,
    }),
    expVolume: {
      itemValueResolver: ({ expectedVol }) =>
        expectedVol === null ? -Infinity : expectedVol,
      columnLabel: "Exp vol.",
      width: "90px",
      renderCell: ({ item: { expectedVol } }) => (
        <Txt>
          <Indent>{formatThousandsCheckNull(expectedVol)}</Indent>
        </Txt>
      ),
    },
  },
  columnGroups: {
    customerInfo: {
      columnOrder: advancedInfoColumnOrder,
      label: "Customer info",
    },
    noLabel: {
      columnOrder: noLabelColumnOrder,
      borderLeft: true,
    },
    routeSailingAndUnit: {
      columnOrder: routeSailingAndUnitColumnOrder,
      borderLeft: true,
      label: "Route, sailing and unit",
    },
    avgPrice: {
      columnOrder: avgPriceColumnOrder,
      label: `${currency}/m`,
      borderLeft: true,
    },
    targetRateAndVolumes: {
      columnOrder: targetRateAndVolumesColumnOrder,
      label: "Target, rate and volumes",
      borderLeft: true,
    },
  },
  columnGroupOrder: [
    "customerInfo",
    "noLabel",
    "routeSailingAndUnit",
    "avgPrice",
    "targetRateAndVolumes",
  ],
});
